import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { WifiOff } from '@mui/icons-material';

function formatAgo(ts) {
    if (!ts) return '';
    const ms = Date.now() - ts;
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return ' · just now';
    if (mins < 60) return ` · ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return hrs < 24 ? ` · ${hrs}h ago` : ` · ${Math.floor(hrs / 24)}d ago`;
}

export default function OfflineBanner() {
    const [state, setState] = useState({ offline: false, cachedAt: null });
    const [tick, setTick] = useState(0); // forces "X ago" label to refresh

    const handleOffline = useCallback((e) => {
        setState({ offline: true, cachedAt: e.detail?.cachedAt ?? null });
    }, []);

    const handleOnline = useCallback(() => {
        setState({ offline: false, cachedAt: null });
    }, []);

    useEffect(() => {
        window.addEventListener('tas:server:offline', handleOffline);
        window.addEventListener('tas:server:online', handleOnline);
        return () => {
            window.removeEventListener('tas:server:offline', handleOffline);
            window.removeEventListener('tas:server:online', handleOnline);
        };
    }, [handleOffline, handleOnline]);

    // Refresh the "X ago" label every minute
    useEffect(() => {
        if (!state.offline) return;
        const id = setInterval(() => setTick(t => t + 1), 60000);
        return () => clearInterval(id);
    }, [state.offline]);

    if (!state.offline) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 64,
                left: 0,
                right: 0,
                zIndex: 1200,
                bgcolor: '#92400E',
                color: '#FEF3C7',
                py: '6px',
                px: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            }}
        >
            <WifiOff sx={{ fontSize: 15 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}>
                Server unavailable — showing cached data
                {formatAgo(state.cachedAt)}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={11} sx={{ color: '#FEF3C7' }} />
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#FDE68A' }}>
                    Reconnecting…
                </Typography>
            </Box>
        </Box>
    );
}
